import { useContext, useEffect, useState, useMemo } from 'react';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Paper,
} from '@mui/material';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SportsIcon from '@mui/icons-material/Sports';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import { Chart } from 'react-charts';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { useTranslation } from 'react-i18next';
import { formatPriceByLocale } from '../../../lib/utils';
import { AppContext } from '../../../sporttia/all';
import SttFoldingCubeSpinner from '../../../sporttia/spinners/SttFoldingCubeSpinner';
import { sporttiaChartRamp } from '../../../theme/theme';

type MonthStats = {
    numBookings: number;
    income: number;
    numFollowers: number;
};

type YearStats = {
    [month: number]: MonthStats;
};

type Stats = {
    totalDebt: string;
    totalPurse: string;
    years: {
        [year: number]: YearStats;
    };
};

type HomeCardProps = {
    title: string;
    subtitle: string | number;
    label: string | number;
    icon: React.ReactElement;
};

type ChartDatum = {
    month: string;
    value: number;
};

type ChartSeries = {
    label: string;
    data: ChartDatum[];
};

type HomeChartProps = {
    title: string;
    data: ChartSeries[];
    colors: string[];
    years: string[];
};

export default function SCStats(): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const [stats, setStats] = useState<Stats>();
    const [year] = useState<number>(new Date().getFullYear());
    const [totalBookings, setTotalBookings] = useState<number>(0);
    const [totalIncome, setTotalIncome] = useState<number>(0);
    const [loadedStats, setLoadedStats] = useState<boolean>(true);

    // Los dos primeros peldaños de la rampa de gráficas del sistema de diseño
    // (#4f6b00 → #6b8e00 → #8bb600 → #a0d100 → #baf200), que se recorre en
    // orden y sin saltarse ninguno.
    //
    // Antes eran el mismo lima oscuro con alfa 0,3 y 0,8: bajar la opacidad
    // para separar las series las volvía ilegibles, porque al 0,3 la barra
    // compuesta sobre el blanco del área quedaba en 1,3:1. Opacas y en dos
    // peldaños distintos dan 6,1:1 y 3,7:1.
    const graphColors = sporttiaChartRamp.slice(0, 2);

    useEffect(() => {
        cxt?.setHeader({ title: cxt?.sc?.name ?? '' });
    }, []);

    const scId = cxt?.sc?.id;

    /**
     * Init
     */
    useEffect(() => {
        if (scId) {
            cxt?.api('GET', `/scs/${scId}/stats`, {
                params: {
                    years: [year - 1, year].join(','),
                    bookingsByMonth: true,
                    incomeByMonth: true,
                    followersByMonth: true,
                },
                success: (r: Stats) => {
                    setStats(r);
                    setLoadedStats(false);

                    // Calculate total bookings and income
                    let bks = 0;
                    let income = 0;
                    for (let i = 1; i < 13; i++) {
                        const monthData = r.years?.[year]?.[i];
                        bks += monthData?.numBookings ?? 0;
                        income += Number(monthData?.income) || 0;
                    }
                    setTotalBookings(bks);
                    setTotalIncome(income);
                },
            });
        }
    }, [scId]);

    /**
     * Get stats
     */
    const containsFinancialInfo =
        stats &&
        !Number.isNaN(parseFloat(stats?.totalDebt)) &&
        !Number.isNaN(parseFloat(stats?.totalPurse));

    // Transform data for charts
    const transformDataForChart = (
        dataKey: 'numBookings' | 'income' | 'numFollowers',
    ): ChartSeries[] => {
        if (!stats?.years) return [];

        const result: ChartSeries[] = [];

        // Previous year
        if (stats.years[year - 1]) {
            result.push({
                label: String(year - 1),
                data: Object.keys(stats.years[year - 1]!).map((month) => ({
                    month: t(`month.${month}`),
                    value:
                        Number(
                            stats.years[year - 1]?.[parseInt(month, 10)]?.[
                                dataKey
                            ],
                        ) || 0,
                })),
            });
        }

        // Current year
        if (stats.years[year]) {
            result.push({
                label: String(year),
                data: Object.keys(stats.years[year]).map((month) => ({
                    month: t(`month.${month}`),
                    value:
                        Number(
                            stats.years[year]?.[parseInt(month, 10)]?.[dataKey],
                        ) || 0,
                })),
            });
        }

        return result;
    };

    const bookingsData = useMemo(
        () => transformDataForChart('numBookings'),
        [stats, year, t],
    );
    const incomeData = useMemo(
        () => transformDataForChart('income'),
        [stats, year, t],
    );
    const followersData = useMemo(
        () => transformDataForChart('numFollowers'),
        [stats, year, t],
    );

    const yearsArray = stats?.years ? Object.keys(stats.years) : [];

    // Render
    return (
        <div>
            {loadedStats && (
                <Box sx={{ textAlign: 'center' }} mt={18}>
                    <SttFoldingCubeSpinner active={loadedStats} />
                </Box>
            )}
            {stats && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 3, lg: 2 }}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                                <HomeCard
                                    title={t('Followers')}
                                    subtitle={t('Total')}
                                    label={cxt?.sc?.numFollowers || 0}
                                    icon={<PeopleIcon />}
                                />
                            </Grid>

                            {containsFinancialInfo && (
                                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                                    <HomeCard
                                        title={t('Income')}
                                        subtitle={year}
                                        label={formatPriceByLocale(
                                            Number(
                                                Number(totalIncome).toFixed(2),
                                            ),
                                            cxt?.sc?.city?.country?.currency ??
                                                'EUR',
                                        )}
                                        icon={<AttachMoneyIcon />}
                                    />
                                </Grid>
                            )}

                            {containsFinancialInfo && (
                                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                                    <HomeCard
                                        title={t('Debt')}
                                        subtitle={t('Total')}
                                        label={formatPriceByLocale(
                                            Number(
                                                parseFloat(
                                                    stats.totalDebt,
                                                ).toFixed(2),
                                            ),
                                            cxt?.sc?.city?.country?.currency ??
                                                'EUR',
                                        )}
                                        icon={<SentimentVeryDissatisfiedIcon />}
                                    />
                                </Grid>
                            )}

                            {containsFinancialInfo && (
                                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                                    <HomeCard
                                        title={t('Purse')}
                                        subtitle={t('Total')}
                                        label={formatPriceByLocale(
                                            Number(
                                                parseFloat(
                                                    stats.totalPurse,
                                                ).toFixed(2),
                                            ),
                                            cxt?.sc?.city?.country?.currency ??
                                                'EUR',
                                        )}
                                        icon={<LocalAtmIcon />}
                                    />
                                </Grid>
                            )}

                            <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                                <HomeCard
                                    title={t('Bookings')}
                                    subtitle={year}
                                    label={totalBookings}
                                    icon={<SportsIcon />}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 9, lg: 10 }}>
                        {stats && (
                            <>
                                {bookingsData.length > 0 && (
                                    <HomeChart
                                        title={t('Bookings')}
                                        colors={graphColors}
                                        years={yearsArray}
                                        data={bookingsData}
                                    />
                                )}

                                {containsFinancialInfo &&
                                    incomeData.length > 0 && (
                                        <HomeChart
                                            title={t('Income')}
                                            colors={graphColors}
                                            years={yearsArray}
                                            data={incomeData}
                                        />
                                    )}

                                {followersData.length > 0 && (
                                    <HomeChart
                                        title={t('Followers')}
                                        colors={graphColors}
                                        years={yearsArray}
                                        data={followersData}
                                    />
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            )}
        </div>
    );
}

/**
 * HomeChart
 */
function HomeChart({
    title,
    data,
    colors,
    years,
}: HomeChartProps): React.ReactElement {
    const primaryAxis = useMemo(
        () => ({
            getValue: (datum: ChartDatum) => datum.month,
        }),
        [],
    );

    const secondaryAxes = useMemo(
        () => [
            {
                getValue: (datum: ChartDatum) => datum.value,
                elementType: 'bar' as const,
            },
        ],
        [],
    );

    const chartData = useMemo(() => data, [data]);

    return (
        <Box mb={2}>
            <Paper>
                <Box p={2}>
                    <Box mb={2} display="flex" alignItems="center" gap={2}>
                        <b>{title}</b>
                        <>
                            {years.map((yearItem, i) => (
                                <Box
                                    key={i}
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                >
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: '4px',
                                            backgroundColor: colors[i],
                                        }}
                                    />
                                    <span>{yearItem}</span>
                                </Box>
                            ))}
                        </>
                    </Box>

                    <Box sx={{ height: 200 }}>
                        {chartData.length > 0 && (
                            <Chart
                                options={{
                                    data: chartData,
                                    primaryAxis,
                                    secondaryAxes,
                                    defaultColors: colors,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

/**
 * Counter Card
 */
function HomeCard({
    title,
    subtitle,
    label,
    icon,
}: HomeCardProps): React.ReactElement {
    return (
        <Card>
            <CardHeader
                title={title}
                subheader={subtitle}
                avatar={
                    <Avatar
                        sx={{
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                        }}
                    >
                        {icon}
                    </Avatar>
                }
            />

            <CardContent>
                <Box sx={{ fontSize: 25, textAlign: 'center' }}>{label}</Box>
            </CardContent>
        </Card>
    );
}
